import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCartIcon,
  ClipboardDocumentListIcon,
  UserIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  ChevronUpIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAuthStore } from "../../store/authStore";
import { useGlobalActivities } from "../../hooks/useGlobalActivities";
import { useCartStore } from "../../store/cartStore";
import { useUIStore } from "../../store/uiStore";
import CurrencySelector from "../CurrencySelector";
import {
  formatPrice as formatPriceSyntax,
  getArtworkPrice,
} from "../../utils/currency";
import { useCurrencyStore } from "../../store/currencyStore";
import ProposalModal from "../ProposalModal";

/** Anchored dropdown on desktop; fixed to viewport on tablet/phone so panels never clip off-screen */
const headerDropdownPanelClass =
  "z-[100] w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border border-[#2a2a2a] bg-[#111111] shadow-2xl max-lg:fixed max-lg:right-3 max-lg:top-[3.25rem] lg:absolute lg:right-0 lg:top-full lg:mt-2";

const toolsMenuPanelClass =
  "fixed left-1/2 top-[3.25rem] z-[70] w-[calc(100vw-1.5rem)] max-w-sm -translate-x-1/2 overflow-hidden rounded-xl border border-[#2a2a2a] bg-[#111111] shadow-2xl";

export default function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const {
    getTodayAndFutureCount,
    getTodayAndFutureActivities,
    getTodayOnlyCount,
  } = useGlobalActivities();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showActivitiesDropdown, setShowActivitiesDropdown] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const activitiesDropdownRef = useRef<HTMLDivElement>(null);
  const cartDropdownRef = useRef<HTMLDivElement>(null);
  const toolsMenuRef = useRef<HTMLDivElement>(null);
  const userId = user?.id || "";
  const [showProposalModal, setShowProposalModal] = useState(false);
  const userIdToItems = useCartStore((s) => s.userIdToItems);
  const clearCart = useCartStore((s) => s.clearCart);
  const cartItems = useMemo(
    () => (userId ? userIdToItems[userId] || [] : []),
    [userIdToItems, userId],
  );
  const cartCount = cartItems.length;
  const removeFromCart = useCartStore((s) => s.removeFromCart);
  const syncCart = useCartStore((s) => s.syncFromServer);
  const removingItems = useCartStore((s) => s.removingItems);
  const [showCartDropdown, setShowCartDropdown] = useState(false);

  const hactEnabled = useUIStore((s) => s.hactEnabled);
  const toggleHact = useUIStore((s) => s.toggleHact);
  const hidePrices = useUIStore((s) => s.hidePrices);
  const toggleHidePrices = useUIStore((s) => s.toggleHidePrices);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const unitSystem = useUIStore((s) => s.unitSystem);
  const setUnitSystem = useUIStore((s) => s.setUnitSystem);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const closeAllDropdowns = () => {
    setShowCartDropdown(false);
    setShowActivitiesDropdown(false);
    setShowUserMenu(false);
    setShowToolsMenu(false);
  };

  useEffect(() => {
    if (showProposalModal) {
      closeAllDropdowns();
    }
  }, [showProposalModal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        activitiesDropdownRef.current &&
        !activitiesDropdownRef.current.contains(target) &&
        showActivitiesDropdown
      ) {
        setShowActivitiesDropdown(false);
      }
      if (
        cartDropdownRef.current &&
        !cartDropdownRef.current.contains(target) &&
        showCartDropdown
      ) {
        setShowCartDropdown(false);
      }
      if (
        toolsMenuRef.current &&
        !toolsMenuRef.current.contains(target) &&
        showToolsMenu
      ) {
        setShowToolsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showActivitiesDropdown, showCartDropdown, showToolsMenu]);

  useEffect(() => {
    if (!showToolsMenu) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowToolsMenu(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showToolsMenu]);

  useEffect(() => {
    if (!showCartDropdown) return;
    const doSync = async () => {
      try {
        await syncCart();
      } catch {
        /* ignore */
      }
    };
    doSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCartDropdown]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        await syncCart();
      } catch {
        /* ignore */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const formatPrice = (artwork: { prices?: unknown }): string => {
    const currency = useCurrencyStore.getState().selectedCurrency;
    const { price, currency: displayCurrency } = getArtworkPrice(
      artwork,
      currency,
    );
    return formatPriceSyntax(price, displayCurrency);
  };


  
  const unitToggleButtons = (fullWidth = false) => (
    <div className={`flex gap-2 ${fullWidth ? "w-full" : ""}`}>
      <button
        type="button"
        onClick={() => setUnitSystem("cm")}
        className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
          fullWidth ? "flex-1" : ""
        } ${
          unitSystem === "cm"
            ? "border-[#c9a84c] bg-[#2a2418] text-[#c9a84c]"
            : "border-[#2a2a2a] bg-[#0a0a0a] text-[#999999] hover:bg-[#161616]"
        }`}
      >
        CM
      </button>
      <button
        type="button"
        onClick={() => setUnitSystem("inch")}
        className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
          fullWidth ? "flex-1" : ""
        } ${
          unitSystem === "inch"
            ? "border-[#c9a84c] bg-[#2a2418] text-[#c9a84c]"
            : "border-[#2a2a2a] bg-[#0a0a0a] text-[#999999] hover:bg-[#161616]"
        }`}
      >
        INCH
      </button>
    </div>
  );

  const haucHprToggles = (fullWidth = false) => (
    <div
      className={`flex gap-2 ${fullWidth ? "w-full" : ""}`}
    >
      <button
        type="button"
        onClick={() => toggleHact()}
        className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
          fullWidth ? "flex-1" : ""
        } ${
          hactEnabled
            ? "border-red-900 bg-[#2a1111] text-red-400 hover:bg-[#3a1717]"
            : "border-green-900 bg-[#0f1f13] text-green-400 hover:bg-[#152a1a]"
        }`}
      >
        HAUC
      </button>
      <button
        type="button"
        onClick={toggleHidePrices}
        title="Hide Prices for client view"
        className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
          fullWidth ? "flex-1" : ""
        } ${
          hidePrices
            ? "border-red-900 bg-[#2a1111] text-red-400 hover:bg-[#3a1717]"
            : "border-green-900 bg-[#0f1f13] text-green-400 hover:bg-[#152a1a]"
        }`}
      >
        HPR
      </button>
    </div>
  );

  return (
    <header className="shrink-0 border-b border-[#2a2a2a] bg-[#111111] px-3 py-2 shadow-sm sm:px-4 sm:py-3 lg:px-6">
      <div className="flex flex-col gap-2 lg:gap-0">
        {/* Row 1: brand + primary actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              onClick={toggleSidebar}
              className="shrink-0 text-[#999999] hover:text-[#c9a84c] lg:hidden"
              aria-label="Open menu"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="truncate text-sm font-bold tracking-wide text-[#c9a84c] hover:text-[#e0bc5c] lg:text-base"
            >
              ART GALLERY
            </button>
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {/* Tools — mobile & tablet only */}
            <div className="relative lg:hidden" ref={toolsMenuRef}>
              <button
                type="button"
                onClick={() => {
                  setShowToolsMenu(!showToolsMenu);
                  setShowCartDropdown(false);
                  setShowActivitiesDropdown(false);
                  setShowUserMenu(false);
                }}
                className={`flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors sm:px-3 sm:text-sm ${
                  showToolsMenu
                    ? "border-[#c9a84c] bg-[#2a2418] text-[#c9a84c]"
                    : "border-[#2a2a2a] bg-[#0a0a0a] text-[#999999] hover:bg-[#161616]"
                }`}
                aria-label="Display tools"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4" />
                <span className="hidden lg:inline">Tools</span>
              </button>
              {showToolsMenu && (
                <>
                  <div
                    className="fixed inset-0 z-[60] bg-black/60"
                    onClick={() => setShowToolsMenu(false)}
                    aria-hidden
                  />
                  <div className={`${toolsMenuPanelClass} p-4`}>
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-semibold text-white">
                        Display settings
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowToolsMenu(false)}
                        className="text-[#999999] hover:text-[#c9a84c]"
                        aria-label="Close tools"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="mb-1.5 text-xs font-medium text-[#999999]">
                          Currency
                        </p>
                        <CurrencySelector compact className="w-full" />
                      </div>
                      <div>
                        <p className="mb-1.5 text-xs font-medium text-[#999999]">
                          Size unit
                        </p>
                        {unitToggleButtons(true)}
                      </div>
                      <div>
                        <p className="mb-1.5 text-xs font-medium text-[#999999]">
                          View toggles
                        </p>
                        {haucHprToggles(true)}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Desktop-only inline tools */}
            <div className="hidden items-center gap-2 lg:flex lg:gap-4">
              <CurrencySelector />
              {unitToggleButtons(false)}
              {haucHprToggles(false)}
            </div>

            {/* Cart */}
            <div className="relative shrink-0" ref={cartDropdownRef}>
              <button
                type="button"
                className="relative flex items-center p-1.5 text-[#999999] transition-colors hover:text-[#c9a84c] sm:space-x-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCartDropdown(!showCartDropdown);
                  setShowToolsMenu(false);
                }}
                aria-label="Open cart"
              >
                <ShoppingCartIcon className="h-5 w-5" />
                <span className="hidden text-sm font-medium lg:inline">
                  My cart
                </span>
                {cartCount > 0 && (
                  <span className="absolute -right-0.5 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#c9a84c] text-[10px] font-bold text-black">
                    {cartCount}
                  </span>
                )}
              </button>
              {showCartDropdown && (
                <div className={headerDropdownPanelClass}>
                  <div className="max-h-96 overflow-y-auto">
                    {cartItems.length > 0 ? (
                      cartItems.map((item) => (
                        <div
                          key={item.id}
                          className="cursor-pointer border-b border-[#2a2a2a] px-4 py-3 hover:bg-[#1a1a1a]"
                          onClick={() => navigate(`/artworks/${item.id}`)}
                        >
                          <div className="flex items-center">
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt={item.title}
                                className="mr-3 h-12 w-12 rounded object-cover"
                              />
                            ) : (
                              <div className="mr-3 h-12 w-12 rounded bg-[#1a1a1a]" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-white">
                                {item.title}
                              </p>
                              <p className="truncate text-xs text-[#999999]">
                                {item.artist}
                              </p>
                              <p className="mt-1 text-xs text-[#999999]">
                                {hidePrices
                                  ? ""
                                  : item.prices
                                    ? formatPrice(item)
                                    : "Price on request"}
                                {item.status ? (
                                  <span className="ml-2 inline-flex items-center rounded-full bg-[#0f1f13] px-2 py-0.5 text-[10px] text-green-400">
                                    {item.status}
                                  </span>
                                ) : null}
                              </p>
                            </div>
                            <button
                              type="button"
                              className={`ml-2 text-xs ${
                                removingItems.has(item.id)
                                  ? "cursor-not-allowed text-[#555555]"
                                  : "text-red-400 hover:text-red-300"
                              }`}
                              disabled={removingItems.has(item.id)}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!removingItems.has(item.id)) {
                                  removeFromCart(item.id);
                                }
                              }}
                            >
                              {removingItems.has(item.id)
                                ? "Removing..."
                                : "Remove"}
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-8 text-center">
                        <ShoppingCartIcon className="mx-auto mb-3 h-12 w-12 text-[#333333]" />
                        <p className="text-sm text-[#999999]">
                          Your cart is empty
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-[#2a2a2a] bg-[#111111] p-4">
                    {cartItems.length > 0 && (
                      <button
                        type="button"
                        className="mb-4 w-full text-center text-xs text-red-400 hover:text-red-300"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          clearCart();
                        }}
                      >
                        Remove all
                      </button>
                    )}
                    <button
                      type="button"
                      className="flex w-full items-center justify-center rounded-md bg-[#c9a84c] px-4 py-2 text-xs font-semibold text-black hover:bg-[#b8941f] sm:text-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCartDropdown(false);
                        setShowProposalModal(true);
                      }}
                    >
                      Go to Cart
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* To Do */}
            <div className="relative shrink-0" ref={activitiesDropdownRef}>
              <button
                type="button"
                className="relative flex items-center p-1.5 text-[#999999] transition-colors hover:text-[#c9a84c] sm:space-x-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowActivitiesDropdown(!showActivitiesDropdown);
                  setShowToolsMenu(false);
                }}
                aria-label="Open to-do lists"
              >
                <ClipboardDocumentListIcon className="h-5 w-5" />
                <span className="hidden text-sm font-medium lg:inline">
                  To Do
                </span>
                {getTodayOnlyCount() > 0 && (
                  <span className="absolute -right-0.5 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#c9a84c] text-[10px] font-bold text-black">
                    {getTodayOnlyCount()}
                  </span>
                )}
              </button>
              {showActivitiesDropdown && (
                <div className={headerDropdownPanelClass}>
                  <div className="max-h-80 overflow-y-auto sm:max-h-96">
                    {getTodayAndFutureActivities().length > 0 ? (
                      getTodayAndFutureActivities()
                        .slice(0, 10)
                        .map((activity) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const dueDate = new Date(activity.dueDate);
                          dueDate.setHours(0, 0, 0, 0);
                          const isToday =
                            dueDate.getTime() === today.getTime();
                          const daysDiff = Math.ceil(
                            (dueDate.getTime() - today.getTime()) /
                              (1000 * 3600 * 24),
                          );

                          return (
                            <div
                              key={activity.id}
                              className="cursor-pointer border-b border-[#2a2a2a] px-3 py-2 transition-colors hover:bg-[#1a1a1a] sm:px-4 sm:py-3"
                              onClick={() => {
                                navigate(`/contacts/${activity.resId}`);
                                setShowActivitiesDropdown(false);
                              }}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-xs font-medium text-white sm:text-sm">
                                    {activity.summary}
                                  </p>
                                  <p className="mt-0.5 text-[10px] text-[#999999] sm:text-xs">
                                    {activity.activityType.name} •{" "}
                                    {activity.resName ||
                                      `Contact ${activity.resId}`}
                                  </p>
                                </div>
                                <span
                                  className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium sm:px-2 sm:text-xs ${
                                    isToday
                                      ? "bg-[#2a2010] text-orange-400"
                                      : daysDiff === 1
                                        ? "bg-[#2a2418] text-[#c9a84c]"
                                        : "bg-[#1a1a1a] text-[#999999]"
                                  }`}
                                >
                                  {isToday
                                    ? "Today"
                                    : daysDiff === 1
                                      ? "Tomorrow"
                                      : daysDiff === 2
                                        ? "In 2 days"
                                        : `In ${daysDiff} days`}
                                </span>
                              </div>
                            </div>
                          );
                        })
                    ) : (
                      <div className="px-4 py-8 text-center">
                        <ClipboardDocumentListIcon className="mx-auto mb-2 h-10 w-10 text-[#333333] sm:h-12 sm:w-12" />
                        <p className="text-xs text-[#999999] sm:text-sm">
                          No upcoming activities
                        </p>
                      </div>
                    )}
                  </div>
                  {getTodayAndFutureActivities().length > 10 && (
                    <div className="border-t border-[#2a2a2a] bg-[#161616] px-4 py-3">
                      <button
                        type="button"
                        onClick={() => {
                          navigate("/todos");
                          setShowActivitiesDropdown(false);
                        }}
                        className="text-xs font-medium text-[#c9a84c] hover:text-[#e0bc5c]"
                      >
                        View all {getTodayAndFutureCount()} activities →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Account */}
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUserMenu(!showUserMenu);
                  setShowToolsMenu(false);
                }}
                className="flex items-center p-1.5 text-[#999999] transition-colors hover:text-[#c9a84c] sm:space-x-1"
                aria-label="Open account menu"
              >
                <UserIcon className="h-5 w-5" />
                <span className="hidden text-sm font-medium lg:inline">
                  Account
                </span>
              </button>
              {showUserMenu && (
                <div className="z-[100] w-56 max-w-[min(16rem,calc(100vw-1.5rem))] rounded-lg border border-[#2a2a2a] bg-[#111111] shadow-lg max-lg:fixed max-lg:right-3 max-lg:top-[3.25rem] lg:absolute lg:right-0 lg:top-full lg:mt-2">
                  <div className="border-b border-[#2a2a2a] p-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2a2418]">
                        <UserIcon className="h-6 w-6 text-[#c9a84c]" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-white">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="truncate text-sm text-[#999999]">
                          {user?.email}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="py-2">
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/profile");
                        setShowUserMenu(false);
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-[#cccccc] hover:bg-[#1a1a1a]"
                    >
                      <UserIcon className="mr-3 h-4 w-4" />
                      My Profile
                    </button>
                  </div>
                  <div className="border-t border-[#2a2a2a] py-2">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center px-4 py-2 text-sm text-red-400 hover:bg-[#1a1a1a]"
                    >
                      <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4" />
                      Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ProposalModal
        isOpen={showProposalModal}
        onClose={() => setShowProposalModal(false)}
        initialArtwork={null}
      />

      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
          aria-hidden
        />
      )}
    </header>
  );
}